import { ogImageMeta } from "@/lib/seo/og-image";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ArticlePage } from "@/components/insights/ArticlePage";
import { getPost } from "@/lib/wp.functions";
import { isPostExcludedFromBg } from "@/content/posts/bg-exclusions";
import { LocaleLink } from "@/components/site/LocaleLink";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { urlFor } from "@/lib/seo/schema";
import { blogPostSchemas } from "@/lib/seo/page-schema";
import {
  blogMetaDescription,
  blogMetaTitle,
  canonicalSlugFor,
  stripHtml,
} from "@/lib/seo/blog-meta";

export const Route = createFileRoute("/bg/blog/$slug")({
  loader: async ({ params }) => {
    const { post } = await getPost({ data: { slug: params.slug, locale: "bg" } });
    if (!post) {
      // English-only articles: send shared /bg/blog links to the English article
      // instead of a dead "not found" page.
      if (isPostExcludedFromBg(params.slug)) {
        throw redirect({ to: "/blog/$slug", params: { slug: params.slug }, statusCode: 301 });
      }
      throw notFound();
    }
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return { meta: [{ title: "Статия - Perceptica" }] };
    const title = stripHtml(post.title.rendered);
    const hasAuthoredBg = post.authoredLocale === "bg";
    const desc = blogMetaDescription(post, hasAuthoredBg);
    const image = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
    const shareImage = image?.startsWith("/") ? `https://perceptica.com${image}` : image;
    const canonicalSlug = canonicalSlugFor(post);
    const isDuplicate = canonicalSlug !== post.slug;
    const canonical = urlFor("bg", `/blog/${canonicalSlug}`);
    return {
      meta: [
        { title: blogMetaTitle(post, title, hasAuthoredBg) },
        { name: "description", content: desc },
        ...(isDuplicate || post.seo?.noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        ...ogImageMeta("bg", "/blog", title).map((meta) =>
          shareImage && (("property" in meta && meta.property === "og:image") || ("name" in meta && meta.name === "twitter:image"))
            ? { ...meta, content: shareImage }
            : meta,
        ),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ],
    links: [
    ],
      scripts: blogPostSchemas({
        locale: "bg",
        canonicalSlug,
        title,
        description: desc,
        image: shareImage,
        datePublished: post.date,
        dateModified: post.modified,
        authorName: post._embedded?.author?.[0]?.name,
        contentHtml: post.content?.rendered,
      }),
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-24 text-center lg:px-10">
        <h1 className="font-display text-3xl font-medium text-foreground">Статията не е намерена</h1>
        <p className="mt-4 text-foreground/70">Възможно е да е преместена или непубликувана.</p>
        <LocaleLink to="/blog" className="mt-6 inline-block text-accent underline underline-offset-2">
          Обратно към блога
        </LocaleLink>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-24 text-center lg:px-10">
        <h1 className="font-display text-2xl font-medium text-foreground">Статията не може да бъде заредена</h1>
        <p className="mt-3 text-foreground/70">Възникна грешка при зареждането на статията. Моля, опитайте отново.</p>
        {import.meta.env.DEV && (
          <p className="mt-2 text-[12px] text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        )}
        <LocaleLink to="/blog" className="mt-6 inline-block text-accent underline underline-offset-2">
          Обратно към блога
        </LocaleLink>
      </main>
      <SiteFooter />
    </div>
  ),
  component: ArticlePage,
});
